import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { InspectionTagBadge } from '../components/inspection/InspectionTagBadge';
import { TagRulesManager } from '../components/admin/TagRulesManager';
import { Tag, TrendingUp, Search, Download, Filter, Settings } from 'lucide-react';
import '../styles/modern.css';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ? `${import.meta.env.VITE_API_BASE_URL}/api` : '/api';

interface TagStatistic {
  tagName: string;
  count: number;
}

interface TaggedInspection {
  inspectionId: string;
  dotNumber: number;
  reportNumber: string;
  inspDate: string;
  reportState: string;
  tags: string[];
  carrierName?: string;
}

interface ShipperTag {
  tagName: string;
  displayName: string;
  color: string;
  description: string | null;
}

export const TagsManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [tagStatistics, setTagStatistics] = useState<TagStatistic[]>([]);
  const [availableTags, setAvailableTags] = useState<ShipperTag[]>([]);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [taggedInspections, setTaggedInspections] = useState<TaggedInspection[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingInspections, setLoadingInspections] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [activeTab, setActiveTab] = useState<'statistics' | 'rules'>('statistics');
  const limit = 50;

  useEffect(() => {
    fetchTagStatistics();
    fetchAvailableTags();
  }, []);

  useEffect(() => {
    if (selectedTag) {
      fetchInspectionsByTag();
    }
  }, [selectedTag, offset]);

  const fetchTagStatistics = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/inspection-tags/statistics/all`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setTagStatistics(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch tag statistics:', error);
      setError('Failed to load tag statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableTags = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/inspection-tags/shipper-tags/available`);
      const data = await response.json();
      if (data.success) {
        setAvailableTags(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch available tags:', error);
    }
  };

  const fetchInspectionsByTag = async () => {
    if (!selectedTag) return;

    try {
      setLoadingInspections(true);
      const response = await fetch(
        `${API_BASE_URL}/inspection-tags/by-tag/${encodeURIComponent(selectedTag)}?limit=${limit}&offset=${offset}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      if (data.success) {
        setTaggedInspections(data.data);
        setTotal(data.pagination.total);
      }
    } catch (error) {
      console.error('Failed to fetch tagged inspections:', error);
    } finally {
      setLoadingInspections(false);
    }
  };

  const getTagColor = (tagName: string): string => {
    const tag = availableTags.find(t => t.tagName === tagName);
    return tag?.color || 'var(--color-gray-500)';
  };

  const handleRemoveTag = async (inspectionId: string, tagName: string) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/inspection-tags/${inspectionId}/${encodeURIComponent(tagName)}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );
      const data = await response.json();
      if (data.success) {
        // Refresh both statistics and inspections list
        await fetchTagStatistics();
        if (selectedTag) {
          await fetchInspectionsByTag();
        }
      }
    } catch (error) {
      console.error('Failed to remove tag:', error);
    }
  };

  if (loading) {
    return (
      <>
        <div className="container-spacious">
          <LoadingSpinner />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="container-spacious">
        {/* Header */}
        <div className="fade-in-up" style={{ marginBottom: '1rem' }}>
          <h1 className="heading-1">Inspection Tags Management</h1>
          <p className="text-muted">
            Manually categorize inspections by shipper for better tracking and analysis
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid var(--color-gray-200)', paddingBottom: '0.5rem' }}>
          <button
            onClick={() => setActiveTab('statistics')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.25rem',
              borderRadius: '0.5rem 0.5rem 0 0',
              border: 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'statistics' ? 700 : 500,
              fontSize: '0.9375rem',
              backgroundColor: activeTab === 'statistics' ? 'var(--color-primary)' : 'transparent',
              color: activeTab === 'statistics' ? 'white' : 'var(--color-gray-600)',
              transition: 'all 0.2s'
            }}
          >
            <TrendingUp size={18} />
            Tag Statistics
          </button>
          <button
            onClick={() => setActiveTab('rules')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.25rem',
              borderRadius: '0.5rem 0.5rem 0 0',
              border: 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'rules' ? 700 : 500,
              fontSize: '0.9375rem',
              backgroundColor: activeTab === 'rules' ? 'var(--color-primary)' : 'transparent',
              color: activeTab === 'rules' ? 'white' : 'var(--color-gray-600)',
              transition: 'all 0.2s'
            }}
          >
            <Settings size={18} />
            Normalization Rules
          </button>
        </div>

        {/* Tag Rules Manager Tab */}
        {activeTab === 'rules' && (
          <div className="card-modern fade-in-up">
            <TagRulesManager />
          </div>
        )}

        {/* Tag Statistics Grid - only show on statistics tab */}
        {activeTab === 'statistics' && (
        <div className="card-modern fade-in-up" style={{ marginBottom: '2rem' }}>
          <h2 className="heading-2" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1.5rem' }}>
            <TrendingUp style={{ color: 'var(--color-primary)' }} />
            Tag Usage Statistics
          </h2>

          {tagStatistics.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-gray-400)' }}>
              <Tag size={48} style={{ margin: '0 auto 1rem' }} />
              <p>No inspections have been tagged yet.</p>
              <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                Start by visiting a carrier profile and tagging their inspections.
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1rem' }}>
              {tagStatistics.map((stat) => {
                const tagColor = getTagColor(stat.tagName);
                const isSelected = selectedTag === stat.tagName;
                return (
                  <div
                    key={stat.tagName}
                    onClick={() => {
                      setSelectedTag(stat.tagName);
                      setOffset(0);
                    }}
                    style={{
                      padding: '1.5rem',
                      border: `2px solid ${isSelected ? tagColor : 'var(--color-gray-200)'}`,
                      borderRadius: 'var(--radius-lg)',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      background: isSelected ? `${tagColor}10` : 'white'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = tagColor;
                        e.currentTarget.style.background = `${tagColor}05`;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = 'var(--color-gray-200)';
                        e.currentTarget.style.background = 'white';
                      }
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <div
                        style={{
                          width: '16px',
                          height: '16px',
                          borderRadius: '4px',
                          backgroundColor: tagColor
                        }}
                      />
                      <span style={{ fontWeight: '700', color: '#23211d', fontSize: '1rem' }}>
                        {stat.tagName}
                      </span>
                    </div>
                    <div style={{ fontSize: '2rem', fontWeight: '700', color: tagColor }}>
                      {stat.count.toLocaleString()}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-500)', marginTop: '4px' }}>
                      inspections tagged
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {tagStatistics.length > 0 && (
            <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--color-gray-100)', borderRadius: '8px', border: '1px solid var(--color-primary)' }}>
              <p style={{ fontSize: '0.875rem', color: 'var(--color-gray-800)', margin: 0 }}>
                <strong>Click a tag</strong> to view all inspections with that tag. You can remove tags from individual inspections or manage them in bulk.
              </p>
            </div>
          )}
        </div>
        )}

        {/* Tagged Inspections List */}
        {activeTab === 'statistics' && selectedTag && (
          <div className="card-modern fade-in-up">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
              <h2 className="heading-2" style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: 0 }}>
                <Filter style={{ color: getTagColor(selectedTag) }} />
                {selectedTag} Tagged Inspections
              </h2>
              <button
                onClick={() => {
                  setSelectedTag(null);
                  setTaggedInspections([]);
                  setOffset(0);
                }}
                className="btn-secondary"
                style={{ fontSize: '0.875rem' }}
              >
                Clear Filter
              </button>
            </div>

            {loadingInspections ? (
              <LoadingSpinner />
            ) : taggedInspections.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-gray-400)' }}>
                No inspections found with this tag.
              </div>
            ) : (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table className="table-modern">
                    <thead>
                      <tr>
                        <th style={{ width: '15%' }}>Inspection ID</th>
                        <th style={{ width: '10%' }}>DOT</th>
                        <th style={{ width: '25%' }}>Carrier</th>
                        <th style={{ width: '12%' }}>Date</th>
                        <th style={{ width: '8%' }}>State</th>
                        <th style={{ width: '15%' }}>Report #</th>
                        <th style={{ width: '15%' }}>Tags</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taggedInspections.map((inspection) => (
                        <tr
                          key={inspection.inspectionId}
                          style={{ cursor: 'pointer' }}
                          className="hover:bg-gray-50"
                        >
                          <td style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                            {inspection.inspectionId}
                          </td>
                          <td
                            onClick={() => navigate(`/carrier/${inspection.dotNumber}`)}
                            style={{ color: 'var(--color-primary)', fontWeight: '600', cursor: 'pointer' }}
                          >
                            {inspection.dotNumber}
                          </td>
                          <td style={{ fontWeight: '600' }}>
                            {inspection.carrierName || 'Unknown'}
                          </td>
                          <td>{new Date(inspection.inspDate).toLocaleDateString()}</td>
                          <td>
                            <span className="badge-modern" style={{ background: 'var(--color-gray-100)', color: 'var(--color-gray-900)' }}>
                              {inspection.reportState}
                            </span>
                          </td>
                          <td style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                            {inspection.reportNumber}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                              {inspection.tags.map((tag) => (
                                <InspectionTagBadge
                                  key={tag}
                                  tagName={tag}
                                  color={getTagColor(tag)}
                                  onRemove={() => handleRemoveTag(inspection.inspectionId, tag)}
                                />
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {total > limit && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--color-gray-200)' }}>
                    <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-500)' }}>
                      Showing {offset + 1}-{Math.min(offset + limit, total)} of {total.toLocaleString()} inspections
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => setOffset(Math.max(0, offset - limit))}
                        disabled={offset === 0}
                        className="btn-secondary"
                        style={{ fontSize: '0.875rem' }}
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setOffset(offset + limit)}
                        disabled={offset + limit >= total}
                        className="btn-secondary"
                        style={{ fontSize: '0.875rem' }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Info Box */}
        {activeTab === 'statistics' && !selectedTag && tagStatistics.length > 0 && (
          <div className="card-modern fade-in-up" style={{ background: 'var(--color-gray-100)', border: '1px solid var(--color-primary)', marginTop: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
              <Tag size={20} style={{ color: 'var(--color-primary)', marginTop: '2px' }} />
              <div>
                <h3 style={{ fontWeight: '600', color: 'var(--color-gray-900)', marginBottom: '8px' }}>
                  How to Use Tags
                </h3>
                <ul style={{ color: 'var(--color-gray-800)', fontSize: '0.875rem', lineHeight: '1.6', margin: 0, paddingLeft: '20px' }}>
                  <li>Click a tag above to view all inspections with that tag</li>
                  <li>Remove tags by clicking the × on the badge in the list</li>
                  <li>Visit carrier profiles to tag additional inspections</li>
                  <li>Use tags to filter and organize inspections by shipper</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TagsManagementPage;
